import { RESPONSE_TYPES } from "@root/constants";
import { HttpService } from "@services/http";
import {
  prepareErrorResponse,
  prepareResponseObject,
} from "@services/http/response";
import { AxiosResponse } from "axios";
import {
  MULTI_AI_CHAT_ENDPOINT,
  PDF_PREVIEW,
  PDF_URL,
  USER_CHAT_GET_GOVT_RULES_ENDPOINT,
  USER_CHAT_GET_IMAGES_DESC_ENDPOINT,
  USER_CHAT_GET_REAL_ACC_ENDPOINT,
  USER_CHAT_QUERY_ENDPOINT,
  USER_CHAT_RISKCHECKER_ENDPOINT,
  CHAT_FILE_PATH_ENDPOINT,
} from "./endpoints";

export class ChatService extends HttpService {
  sendInput = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    withPdf: boolean = false,
    TID?: number
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = withPdf
        ? `${baseAuthUrl}/api/user/chat?project_id=${TID}`
        : `${baseAuthUrl}/${USER_CHAT_QUERY_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  sendInputProject = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    withPdf: boolean = false,
    TID?: number
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = withPdf
        ? `${baseAuthUrl}/api/user/project-chatbot?project_id=${TID}`
        : `${baseAuthUrl}/api/user/project-chatbot`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  sendInputMultiDoc = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    withPdf: boolean = false,
    TID?: number
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = withPdf
        ? `${baseAuthUrl}/api/user/multiple-document-query?project_id=${TID}`
        : `${baseAuthUrl}/api/user/multiple-document-query`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  sendInputDrawingAnalysis = async (
    baseAuthUrl: string,
    data: Record<string, TObject>,
    withPdf: boolean = false,
    TID?: number
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }
    try {
      const url = `${baseAuthUrl}/api/user/drawing-chatbot`;
      const apiResponse = await this.post(url, data);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  sendInputKCS = async (
    baseAuthUrl: string,
    data: Record<string, TObject>
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }
    try {
      const url = `${baseAuthUrl}/api/user/congpt`;
      const apiResponse = await this.post(url, data);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  sendMultiAIChat = async (
    baseAuthUrl: string,
    data: {
      query: string;
      type: number;
      chatType: number;
      chatId?: string;
      threadId?: string;
    }
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }
    try {
      const url = `${baseAuthUrl}/${MULTI_AI_CHAT_ENDPOINT}`;
      const apiResponse = await this.post(url, data);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendMultiAIChat:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  riskCheckerCall = async (
    baseAuthUrl: string,
    data: any
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_RISKCHECKER_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };
  getImagesDesc = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_GET_IMAGES_DESC_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };
  get_govt_rules = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_GET_GOVT_RULES_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };
  get_real_accidents = async (
    baseAuthUrl: string,
    data: any
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${USER_CHAT_GET_REAL_ACC_ENDPOINT}`;

      const apiResponse = await this.post(url, data);

      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  sendReport = async (
    baseAuthUrl: string,
    data: Record<string, TObject>
  ): Promise<TObject> => {
    try {
      if (data) {
        const apiResponse = await this.post(
          `${baseAuthUrl}/api/feedback`,
          data,
          { headers: { removeLanguage: true } as any }
        );
        return apiResponse;
      }
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getThreads = async (baseAuthUrl: string, payload: any): Promise<any> => {
    try {
      // const apiResponse = await this.get(`${baseAuthUrl}/api/threads`, payload);
      // return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getThreadByID = async (
    baseAuthUrl: string,
    threadId: string,
    payload: any,
    pid: string | number
  ): Promise<any> => {
    try {
      // const apiResponse = await this.get(
      //   `${baseAuthUrl}/api/chat/${threadId}`,
      //   payload
      // );
      // return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getThreadByUID = async (
    baseAuthUrl: string,
    threadId: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/chat-by-kuid/${threadId}`,
        payload
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getRecentHistory = async (
    baseAuthUrl: string,
    threadId: string
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/thead/recent-chat?threadId=${threadId}`
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getThreadByKUID = async (
    baseAuthUrl: string,
    threadId: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/chat-by-kuid/${threadId}`,
        payload
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  deleteThreads = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    try {
      const apiResponse = await this.delete(
        `${baseAuthUrl}/api/thread`,
        undefined,
        undefined,
        data
      );

      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  bulkDeleteThreads = async (baseAuthUrl: string): Promise<TObject> => {
    try {
      const apiResponse = await this.delete(`${baseAuthUrl}/api/chat-history`);

      return apiResponse;
    } catch (error) {
      return error;
    }
  };

  getUserProfile = async (baseAuthUrl: string): Promise<any> => {
    try {
      // const apiResponse = await this.get(`${baseAuthUrl}/api/user/profile`);
      // return apiResponse;
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getDownloadChat = async (
    baseAuthUrl: string,
    data: any
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("data payload is required.");
    }

    try {
      let url = `${baseAuthUrl}/api/question-pdf`;
      const apiResponse = await this.post(url, data, {
        responseType: "blob" as unknown as any,
      } as any);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  getPdfPreview = async (
    baseAuthUrl: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/${PDF_PREVIEW}`,
        payload
      );
      return apiResponse;
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  exportChats = async (
    baseAuthUrl: string,
    data: { chatIds: number[]; gmtOffset: number }
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/api/chats/export-chat`;
      const apiResponse = await this.post(url, data, {
        // Allow binary downloads if API returns a file
        responseType: "blob" as unknown as any,
      } as any);
      return apiResponse;
    } catch (error) {
      console.error("Error in exportChats:", error);
      throw error;
    }
  };
  isPdfUrlValid = async (baseAuthUrl: string, data: any): Promise<TObject> => {
    if (!data) {
      throw new Error("data payload is required.");
    }

    try {
      let url = `${baseAuthUrl}/${PDF_URL}`;
      const apiResponse = await this.post(url, data);
      return apiResponse;
    } catch (error) {
      console.error("Error in sendInput:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  downloadThreadPdf = async (
    baseAuthUrl: string,
    payload: any
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/thread/${payload?.threadId}/pdf`,
        { chatId: payload?.chatId }
      );
      return apiResponse;
    } catch (error) {
      throw prepareErrorResponse(error);
    }
  };

  getChatFilePath = async (
    baseAuthUrl: string,
    data: { filePath: any }
  ): Promise<TObject> => {
    if (!data) {
      throw new Error("Data payload is required.");
    }

    try {
      const url = `${baseAuthUrl}/${CHAT_FILE_PATH_ENDPOINT}`;
      const apiResponse = await this.post(url, data);
      return apiResponse;
    } catch (error) {
      console.error("Error in getChatFilePath:", error);
      throw error;
    }
  };

  /** The signed-in user's conversations for one chatbot, newest first. */
  getThreadsByChatType = async (
    baseAuthUrl: string,
    chatType: number,
    limit: number = 50
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.get(`${baseAuthUrl}/api/threads`, {
        chatType,
        limit,
      });
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getChatsByThread = async (
    baseAuthUrl: string,
    threadId: string,
    limit: number = 50
  ): Promise<TObject> => {
    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/chats/thread/${threadId}`,
        { limit }
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  stopChat = async (
    baseAuthUrl: string,
    chatId: string
  ): Promise<IPrepareResponse<AxiosResponse>> => {
    if (!chatId) {
      throw new Error("Chat ID is required.");
    }

    try {
      const apiResponse = await this.get(
        `${baseAuthUrl}/api/chats/stop/${chatId}`,
        {}
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      console.error("Error in stopChat:", error);
      throw prepareErrorResponse(error);
    }
  };
}
